import { Box, Typography } from '@mui/material';
import { useEffect, useState } from 'react';
import toast from 'react-hot-toast';
import {
  createOth360User,
  deleteOth360User,
  listOth360UsersPaged,
  type Oth360User,
  type Oth360UsersSortBy,
  type Pagination,
  type SortDir,
} from 'src/api/oth360';
import styles from './Admin360Page.module.scss';
import { UserModal } from './UserModal';
import UsersTable, { User360Evaluation } from './UsersTable';

function pickLeaderDone(u: Oth360User): boolean | null {
  const v =
    u.leaderDone ??
    u.selfHasSubmitted ??
    u.leaderHasSubmitted ??
    u.hasSubmitted ??
    null;
  return typeof v === 'boolean' ? v : null;
}

function pickEmployeeAnswers(u: Oth360User): number | null {
  const v =
    u.employeeAnswersCount ??
    u.employeeResponsesCount ??
    u.answersCount ??
    u.employeesCount ??
    null;
  return typeof v === 'number' && Number.isFinite(v) ? v : null;
}

function toRow(u: Oth360User): User360Evaluation {
  return {
    id: u.id,
    subjectName: u.subjectName,
    subjectEmail: u.subjectEmail,
    leaderLink: u.selfLink,
    employeeLink: u.employeeLink,
    leaderDone: pickLeaderDone(u),
    answersCount: pickEmployeeAnswers(u),
    createdAt: u.createdAt,
  };
}

type CreateUserInput = Omit<
  User360Evaluation,
  | 'id'
  | 'createdAt'
  | 'leaderLink'
  | 'employeeLink'
  | 'leaderDone'
  | 'answersCount'
>;

type StatVariant = 'blue' | 'green' | 'purple';

function StatCard(props: {
  variant: StatVariant;
  title: string;
  value: string;
  subtitle: string;
  iconPathD: string;
}) {
  return (
    <div className={`${styles.statCard} ${styles[props.variant]}`}>
      <div>
        <div className={styles.statHeader}>
          <svg
            className={styles.statIcon}
            fill="none"
            stroke="currentColor"
            viewBox="0 0 24 24"
          >
            <path
              strokeLinecap="round"
              strokeLinejoin="round"
              strokeWidth={2}
              d={props.iconPathD}
            />
          </svg>
          <h4 className={styles.statTitle}>{props.title}</h4>
        </div>

        <p className={styles.statValue}>{props.value}</p>
        <p className={styles.statDesc}>{props.subtitle}</p>
      </div>
    </div>
  );
}

export const Admin360Page = () => {
  const [users, setUsers] = useState<User360Evaluation[]>([]);
  const [pagination, setPagination] = useState<Pagination | null>(null);
  const [page, setPage] = useState(1);
  const [pageSize, setPageSize] = useState(20);
  const [sortBy, setSortBy] = useState<Oth360UsersSortBy>('createdAt');
  const [sortDir, setSortDir] = useState<SortDir>('desc');
  const [isOpenModal, setIsOpenModal] = useState(false);
  const [selectedUser, setSelectedUser] = useState<User360Evaluation | null>(
    null
  );
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [isLoading, setIsLoading] = useState(false);
  const [allUsersForStats, setAllUsersForStats] = useState<
    User360Evaluation[] | null
  >(null);

  // track per-row delete loading
  const [deletingIds, setDeletingIds] = useState<Record<string, boolean>>({});

  const statsUsers = allUsersForStats ?? users;
  const hasLeaderDoneData = statsUsers.some((u) => u.leaderDone !== null);
  const hasAnswersData = statsUsers.some((u) => u.answersCount !== null);

  const leadersDone = hasLeaderDoneData
    ? statsUsers.reduce((acc, u) => acc + (u.leaderDone === true ? 1 : 0), 0)
    : null;

  const employeeAnswersTotal = hasAnswersData
    ? statsUsers.reduce(
        (acc, u) =>
          acc + (typeof u.answersCount === 'number' ? u.answersCount : 0),
        0
      )
    : null;

  const medianAnswersPerLeaderDone = (() => {
    const counts = statsUsers
      .filter(
        (u) =>
          u.leaderDone === true &&
          typeof u.answersCount === 'number' &&
          u.answersCount >= 1
      )
      .map((u) => u.answersCount as number);
    if (counts.length === 0) {
      return null;
    }
    const sorted = [...counts].sort((a, b) => a - b);
    const mid = Math.floor(sorted.length / 2);
    return sorted.length % 2 === 1
      ? sorted[mid]
      : (sorted[mid - 1] + sorted[mid]) / 2;
  })();

  const onRequestSort = (next: Oth360UsersSortBy) => {
    const defaultDir: SortDir = next === 'createdAt' ? 'desc' : 'asc';
    if (sortBy === next) {
      setSortDir((d) => (d === 'asc' ? 'desc' : 'asc'));
    } else {
      setSortBy(next);
      setSortDir(defaultDir);
    }
    setPage(1);
  };

  useEffect(() => {
    let cancelled = false;

    async function load() {
      try {
        setIsLoading(true);
        const { result, pagination } = await listOth360UsersPaged({
          page,
          pageSize,
          sortBy,
          sortDir,
        });
        if (cancelled) {
          return;
        }
        setUsers(result.map(toRow));
        setPagination(pagination);
      } catch (e) {
        toast.error('Failed to load 360 users.');
      } finally {
        if (!cancelled) {
          setIsLoading(false);
        }
      }
    }

    load();
    return () => {
      cancelled = true;
    };
  }, [page, pageSize, sortBy, sortDir]);

  // Fetch all users for stats (leaders done, employee answers total, median)
  useEffect(() => {
    const total = pagination?.totalCount ?? 0;
    if (total === 0) {
      setAllUsersForStats(null);
      return;
    }

    let cancelled = false;

    async function loadAll() {
      try {
        const { result } = await listOth360UsersPaged({
          page: 1,
          pageSize: total,
          sortBy,
          sortDir,
        });
        if (!cancelled) {
          setAllUsersForStats(result.map(toRow));
        }
      } catch {
        if (!cancelled) {
          setAllUsersForStats(null);
        }
      }
    }

    loadAll();
    return () => {
      cancelled = true;
    };
  }, [pagination?.totalCount, sortBy, sortDir]);

  const handleAddUser = () => {
    setSelectedUser(null);
    setIsOpenModal(true);
  };

  const handleEditUser = (user: User360Evaluation) => {
    setSelectedUser(user);
    setIsOpenModal(true);
  };

  const handleDeleteUser = async (user: User360Evaluation) => {
    if (deletingIds[user.id]) {
      return;
    }

    const shouldDelete = window.confirm(
      `Delete user "${user.subjectName}" (${user.subjectEmail})?`
    );
    if (!shouldDelete) {
      return;
    }

    // optimistic remove
    const prevUsers = users;
    setUsers((prev) => prev.filter((u) => u.id !== user.id));
    setDeletingIds((prev) => ({ ...prev, [user.id]: true }));

    try {
      await deleteOth360User(user.id);
      toast.success('360 evaluation deleted.');
      const { result, pagination } = await listOth360UsersPaged({
        page,
        pageSize,
        sortBy,
        sortDir,
      });
      if (result.length === 0 && page > 1) {
        setPage(page - 1);
      } else {
        setUsers(result.map(toRow));
        setPagination(pagination);
      }
    } catch (e) {
      // rollback
      setUsers(prevUsers);
      toast.error('Failed to delete user.');
    } finally {
      setDeletingIds((prev) => {
        const next = { ...prev };
        delete next[user.id];
        return next;
      });
    }
  };

  const handleSubmitUser = async (nextUser: CreateUserInput) => {
    if (selectedUser) {
      setUsers((prev) =>
        prev.map((u) => (u.id === selectedUser.id ? { ...u, ...nextUser } : u))
      );
      setIsOpenModal(false);
      return;
    }

    try {
      setIsSubmitting(true);
      await createOth360User({
        subjectName: nextUser.subjectName,
        subjectEmail: nextUser.subjectEmail,
      });
      toast.success('User created.');
      setIsOpenModal(false);
      if (page === 1) {
        const { result, pagination } = await listOth360UsersPaged({
          page: 1,
          pageSize,
          sortBy,
          sortDir,
        });
        setUsers(result.map(toRow));
        setPagination(pagination);
      } else {
        setPage(1);
      }
    } catch (e) {
      toast.error('Failed to create user.');
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleImportUsers = async (importUsers: CreateUserInput[]) => {
    if (importUsers.length === 0) {
      return;
    }

    try {
      setIsSubmitting(true);

      const results = await Promise.allSettled(
        importUsers.map((nextUser) =>
          createOth360User({
            subjectName: nextUser.subjectName,
            subjectEmail: nextUser.subjectEmail,
          })
        )
      );

      const successCount = results.filter(
        (result) => result.status === 'fulfilled'
      ).length;
      const failedCount = results.length - successCount;

      if (successCount > 0) {
        toast.success(
          `Imported ${successCount} user${successCount === 1 ? '' : 's'}.`
        );
        setIsOpenModal(false);
      }

      if (failedCount > 0) {
        toast.error(
          `${failedCount} user${failedCount === 1 ? '' : 's'} failed to import.`
        );
      }

      if (successCount > 0) {
        if (page === 1) {
          const { result, pagination } = await listOth360UsersPaged({
            page: 1,
            pageSize,
            sortBy,
            sortDir,
          });
          setUsers(result.map(toRow));
          setPagination(pagination);
        } else {
          setPage(1);
        }
      }
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <div className={styles.container}>
      <Box className={styles.titleRow}>
        <Typography variant="h5" sx={{ fontWeight: 900 }}>
          360 Evaluation Users
        </Typography>
      </Box>

      <Box className={styles.headerRow}>
        <Box className={styles.statsSection}>
          <Box className={styles.statsGrid}>
            <StatCard
              variant="blue"
              title="Leaders done"
              value={leadersDone === null ? '—' : String(leadersDone)}
              subtitle={`of ${pagination?.totalCount ?? '—'} total`}
              iconPathD="M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z"
            />
            <StatCard
              variant="green"
              title="Employee answers"
              value={
                employeeAnswersTotal === null
                  ? '—'
                  : String(employeeAnswersTotal)
              }
              subtitle="Total"
              iconPathD="M17 20h5v-2a3 3 0 00-5.356-1.857M17 20H7m10 0v-2c0-.656-.126-1.283-.356-1.857M7 20H2v-2a3 3 0 015.356-1.857M7 20v-2c0-.656.126-1.283.356-1.857m0 0a5.002 5.002 0 019.288 0M15 7a3 3 0 11-6 0 3 3 0 016 0zm6 3a2 2 0 11-4 0 2 2 0 014 0zM7 10a2 2 0 11-4 0 2 2 0 014 0z"
            />
            <StatCard
              variant="purple"
              title="Median answers"
              value={
                medianAnswersPerLeaderDone === null
                  ? '—'
                  : medianAnswersPerLeaderDone.toFixed(1)
              }
              subtitle="Per leader (done)"
              iconPathD="M9 19v-6a2 2 0 00-2-2H5a2 2 0 00-2 2v6a2 2 0 002 2h2a2 2 0 002-2zm0 0V9a2 2 0 012-2h2a2 2 0 012 2v10m-6 0a2 2 0 002 2h2a2 2 0 002-2m0 0V5a2 2 0 012-2h2a2 2 0 012 2v14a2 2 0 01-2 2h-2a2 2 0 01-2-2z"
            />
          </Box>
        </Box>
      </Box>

      <UserModal
        isOpenModal={isOpenModal}
        setIsOpenModal={setIsOpenModal}
        user={selectedUser}
        isSubmitting={isSubmitting}
        onSubmit={handleSubmitUser}
        onImportSubmit={handleImportUsers}
      />

      <div className={styles.scrollableContent}>
        {!isLoading && users.length === 0 && (
          <div style={{ padding: 16 }}>No 360 users found.</div>
        )}
        <UsersTable
          users={users}
          onEditUser={handleEditUser}
          onDeleteUser={handleDeleteUser}
          deletingIds={deletingIds}
          pagination={pagination}
          isLoading={isLoading}
          onAddUser={handleAddUser}
          onPageChange={(nextPage) => setPage(nextPage)}
          onPageSizeChange={(nextPageSize) => {
            setPage(1);
            setPageSize(nextPageSize);
          }}
          sortBy={sortBy}
          sortDir={sortDir}
          onRequestSort={onRequestSort}
        />
      </div>
    </div>
  );
};
